import React, { ChangeEvent, FormEvent, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { toast } from "react-toastify";
import { useImportarProvaResultadoMutation } from "shared/features/api/provaResultado/provaResultadoSlice";
import { useImportarResultadoQuestionarioMutation } from "shared/features/api/resultadoQuestionario/resultadoQuestionarioSlice";

interface ImportacaoArquivoProps {
  titulo: string;
  descricao: string;
  arquivo: File | null;
  isLoading?: boolean;
  submitLabel: string;
  disabled?: boolean;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const ImportacaoArquivo: React.FC<ImportacaoArquivoProps> = ({
  titulo,
  descricao,
  arquivo,
  isLoading = false,
  submitLabel,
  disabled = false,
  onFileChange,
  onSubmit,
}) => {
  const isInvalidCsv = !!arquivo && !arquivo.name.toLowerCase().endsWith(".csv");

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" fontWeight={600}>
          {titulo}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {descricao}
        </Typography>
      </Box>

      <Box component="form" onSubmit={onSubmit}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<DescriptionOutlinedIcon />}
          fullWidth
          sx={{
            justifyContent: "flex-start",
            py: 1.5,
            textTransform: "none",
          }}
          disabled={isLoading || disabled}
        >
          {arquivo ? arquivo.name : "Selecionar arquivo CSV"}
          <input
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={onFileChange}
          />
        </Button>

        {isInvalidCsv && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            O arquivo selecionado deve estar no formato CSV.
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<FileUploadOutlinedIcon />}
            disabled={isLoading || disabled || !arquivo}
          >
            {submitLabel}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export const ImportarProva: React.FC = () => {
  const [arquivoQuestionario, setArquivoQuestionario] = useState<File | null>(null);
  const [arquivoProva, setArquivoProva] = useState<File | null>(null);
  const [importarResultadoQuestionario, { isLoading: isImportandoQuestionario }] =
    useImportarResultadoQuestionarioMutation();
  const [importarProvaResultado, { isLoading: isImportandoProva }] =
    useImportarProvaResultadoMutation();

  const handleQuestionarioFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setArquivoQuestionario(file);
  };

  const handleProvaFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setArquivoProva(file);
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    const response = error as {
      data?: {
        errors?: string[];
        message?: string;
      };
    };

    return (
      response.data?.errors?.[0] ||
      response.data?.message ||
      fallback
    );
  };

  const handleQuestionarioSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!arquivoQuestionario) {
      toast.warning("Selecione um arquivo CSV para importar.");
      return;
    }

    if (!arquivoQuestionario.name.toLowerCase().endsWith(".csv")) {
      toast.warning("O arquivo selecionado deve estar no formato CSV.");
      return;
    }

    await toast.promise(importarResultadoQuestionario(arquivoQuestionario).unwrap(), {
      pending: "Importando questionário...",
      success: "Questionário comportamental importado com sucesso!",
      error: {
        render({ data }) {
          return getErrorMessage(data, "Erro ao importar o arquivo do questionário comportamental.");
        },
      },
    });

    setArquivoQuestionario(null);
  };

  const handleProvaSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!arquivoProva) {
      toast.warning("Selecione um arquivo CSV para importar.");
      return;
    }

    if (!arquivoProva.name.toLowerCase().endsWith(".csv")) {
      toast.warning("O arquivo selecionado deve estar no formato CSV.");
      return;
    }

    await toast.promise(importarProvaResultado(arquivoProva).unwrap(), {
      pending: "Importando prova...",
      success: "Prova importada com sucesso!",
      error: {
        render({ data }) {
          return getErrorMessage(data, "Erro ao importar o arquivo da prova.");
        },
      },
    });

    setArquivoProva(null);
  };

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        px: { xs: 1, sm: 2, md: 4 },
        py: { xs: 1, sm: 2 },
      }}
    >
      <Grid item xs={12} md={8} lg={6}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <ImportacaoArquivo
            titulo="Questionário comportamental"
            descricao="Selecione o arquivo CSV com os resultados do questionário comportamental."
            arquivo={arquivoQuestionario}
            isLoading={isImportandoQuestionario}
            submitLabel="Importar questionário"
            onFileChange={handleQuestionarioFileChange}
            onSubmit={handleQuestionarioSubmit}
          />

          <ImportacaoArquivo
            titulo="Prova"
            descricao="Selecione o arquivo CSV com os resultados da prova para envio."
            arquivo={arquivoProva}
            isLoading={isImportandoProva}
            submitLabel="Importar prova"
            onFileChange={handleProvaFileChange}
            onSubmit={handleProvaSubmit}
          />
        </Box>
      </Grid>
    </Grid>
  );
};
